import React, { useState } from 'react';
import {
  Button,
  DataTable,
  Loading,
  Tag,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { Add, Edit, TrashCan } from '@carbon/react/icons';

import {
  useGetHolidaysQuery,
  useDeleteHolidayMutation,
} from '@/redux/new_services/holidays';

import HolidayForm from './HolidayForm';

// Top-level admin page for the holiday master records. Lists every holiday
// with its federal-state coverage and exposes create / edit / delete.
const HolidaysPage = () => {
  const { data: holidays = [], isLoading } = useGetHolidaysQuery();
  const [deleteHoliday] = useDeleteHolidayMutation();

  const [editing, setEditing] = useState(null); // null = new, object = edit
  const [formOpen, setFormOpen] = useState(false);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (h) => {
    setEditing(h);
    setFormOpen(true);
  };
  const closeForm = () => setFormOpen(false);

  const handleDelete = async (h) => {
    if (!confirm(`Feiertag "${h.name}" wirklich löschen?`)) return;
    try {
      await deleteHoliday(h.model.ID).unwrap();
    } catch (e) {
      alert('Löschen fehlgeschlagen');
    }
  };

  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'recurrence', header: 'Typ' },
    { key: 'federalStates', header: 'Bundesländer' },
    { key: 'actions', header: '' },
  ];

  const rows = holidays.map((h) => ({
    id: String(h.model.ID),
    name: h.name,
    recurrence: humanRecurrence(h),
    federalStates: (h.federal_states || []).map((s) => s.code).join(', ') || '—',
    raw: h,
  }));

  return (
    <div className="holidays-page">
      <div className="holidays-page__head">
        <h2>Feiertage</h2>
        <Button renderIcon={Add} onClick={openNew}>
          Neuen Feiertag anlegen
        </Button>
      </div>

      {isLoading && <Loading active small withOverlay={false} />}

      <DataTable rows={rows} headers={headers}>
        {({ getTableProps, getHeaderProps, getRowProps }) => (
          <TableContainer>
            <TableToolbar>
              <TableToolbarContent />
            </TableToolbar>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableHeader key={h.key} {...getHeaderProps({ header: h })}>
                      {h.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.recurrence}</TableCell>
                    <TableCell>
                      {row.raw.federal_states?.length > 0 ? (
                        row.raw.federal_states.map((s) => (
                          <Tag key={s.code} type="cool-gray">
                            {s.code}
                          </Tag>
                        ))
                      ) : (
                        <span>—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={Edit}
                        hasIconOnly
                        iconDescription="Bearbeiten"
                        onClick={() => openEdit(row.raw)}
                      />
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={TrashCan}
                        hasIconOnly
                        iconDescription="Löschen"
                        onClick={() => handleDelete(row.raw)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>

      <HolidayForm open={formOpen} holiday={editing} onClose={closeForm} />
    </div>
  );
};

// Translate the recurrence_type + raw recurrence_data into a short German
// summary for the table.
const humanRecurrence = (h) => {
  switch (h.recurrence_type) {
    case 'fixed': {
      try {
        const d = JSON.parse(h.recurrence_data);
        return `Festes Datum: ${d.day}.${d.month}.`;
      } catch {
        return 'Festes Datum';
      }
    }
    case 'easter_relative': {
      try {
        const d = JSON.parse(h.recurrence_data);
        return `Ostern ${d.offset_days >= 0 ? '+' : ''}${d.offset_days} Tage`;
      } catch {
        return 'Relativ zu Ostern';
      }
    }
    case 'advent_relative': {
      try {
        const d = JSON.parse(h.recurrence_data);
        return `1. Advent ${d.offset_days >= 0 ? '+' : ''}${d.offset_days} Tage`;
      } catch {
        return 'Relativ zum 1. Advent';
      }
    }
    case 'buss_und_bettag':
      return 'Buß- und Bettag (automatisch)';
    default:
      return h.recurrence_type || '—';
  }
};

export default HolidaysPage;
